import clsx from "clsx"
import { Children, ReactElement, cloneElement, isValidElement, useEffect, useMemo, useRef } from "react"
import { FieldError, useFormContext, useWatch } from "react-hook-form"

import type { InputHelpTextProps } from "@atoms/InputHelpText/InputHelpText.types"
import { Label } from "@atoms/index"

import Input from "@molecules/Form/inputs/Input/Input"
import type { InputProps } from "@molecules/Form/inputs/Input/Input.types"
import InputInternalWrapper from "@molecules/Form/inputs/InputInternalWrapper/InputInternalWrapper"

import styles from "./InputGroup.module.scss"
import { InputGroupProps, Value } from "./InputGroup.types"

export default function InputGroup(props: InputGroupProps) {
    const { children, label, valueMode, combiner, name } = props
    const {
        setValue,
        control,
        formState: { errors },
    } = useFormContext()

    // Check if the combined field has an error from react-hook-form
    const combinedFieldError =
        valueMode === "combined" && name ? (errors as Record<string, FieldError>)[name] : undefined
    const isInErrorState = Boolean(combinedFieldError)

    const childNames = useMemo(() => {
        const names: string[] = []
        Children.forEach(children, (child) => {
            if (!isValidElement(child)) {
                throw new Error("InputGroup only accepts valid React elements as children")
            }

            const isInputComponent = (child.type as { displayName?: string }).displayName === "Input"

            if (!isInputComponent) {
                throw new Error("InputGroup only accepts Input components as children")
            }

            const childProps = child.props as InputProps
            if (childProps.name) {
                names.push(childProps.name)
            }
        })
        return names
    }, [children])

    const watchedValues = useWatch({ control, name: childNames })
    const prevCombinedValue = useRef<string>("")

    useEffect(() => {
        if (valueMode !== "combined" || !combiner || !name) {
            return
        }
        const arr: Value[] = childNames.map((fieldName, i) => ({
            fieldName,
            value: String((watchedValues ?? [])[i] ?? ""),
        }))
        const combined = combiner(arr)
        const combinedString = String(combined ?? "")
        if (combinedString !== prevCombinedValue.current) {
            prevCombinedValue.current = combinedString
            setValue(name, combinedString, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
        }
    }, [valueMode, combiner, name, childNames, setValue, watchedValues])

    const childrenArray = Children.toArray(children)
    const totalChildren = childrenArray.length

    const modifiedChildren = Children.map(children, (child, index) => {
        if (!isValidElement(child)) {
            return child
        }

        const childElement = child as ReactElement<InputProps>
        let groupPosition: InputProps["groupPosition"]

        if (totalChildren === 1) {
            return child
        } else if (index === 0) {
            groupPosition = "start"
        } else if (index === totalChildren - 1) {
            groupPosition = "end"
        } else {
            groupPosition = "middle"
        }

        return (
            <div className={styles.inputGrouped}>
                {cloneElement(childElement, {
                    ...childElement.props,
                    isGrouped: true,
                    groupPosition,
                    // Apply error state to children when combined field has an error
                    ...(isInErrorState ? { isError: true } : {}),
                })}
            </div>
        )
    })

    const labelIsString = typeof label === "string"
    const firstChild = modifiedChildren?.[0] as ReactElement<{ id?: string }> | undefined

    const groupErrors = useMemo(() => {
        const msgs: Array<InputHelpTextProps> = []

        // Add error from combined field name (when in combined mode)
        if (combinedFieldError?.message && typeof combinedFieldError.message === "string") {
            msgs.push({ message: combinedFieldError.message, status: "error" as const, showIcon: true })
        }

        // Add errors from individual child inputs
        childNames.forEach((childName) => {
            const fieldError = (errors as Record<string, FieldError>)[childName]
            if (fieldError?.message && typeof fieldError.message === "string") {
                msgs.push({ message: fieldError.message, status: "error" as const, showIcon: true })
            }
        })

        return msgs.length ? msgs : null
    }, [errors, childNames, combinedFieldError])

    return (
        <div className={styles.base}>
            {valueMode === "combined" && name ? (
                <div className={styles.hiddenInput}>
                    <Input name={name} type="hidden" size="md" isControlled={false} />
                </div>
            ) : null}

            <InputInternalWrapper
                label={
                    label ? (
                        <Label {...(labelIsString ? { label } : label)} htmlFor={firstChild?.props?.id} />
                    ) : undefined
                }
                inputHelpText={groupErrors}
            >
                <div className={clsx(styles.inputs)}>{modifiedChildren}</div>
            </InputInternalWrapper>
        </div>
    )
}
